import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Stack,
  Chip,
  Card,
  CardContent,
  alpha,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import {
  nombreCompletoAfiliado,
  tieneDatosDeAfiliado,
} from "../utils/afiliado";
import { formatCurrency, formatTipoCama } from "../../shared/utils/formatters";
import { parseDateOnly } from "../../shared/utils/dateUtils";
import { _getDepartamentoInfo as getDepartamentoInfo } from "../../departments/lib/getDepartamentoInfo";
import { fetchCategories, fetchDepartmentInfo } from "../../departments/lib/fetchCategories";
import GroupIcon from "@mui/icons-material/Group";
import { Reservation } from "../types/Reservation";
import { _getDepartamentoInfo as getDepartamentoImagen } from "../../departments/utils/departamentos";
import { differenceInDays } from "date-fns";
import { logger } from "../../../utils/logger";
import { Z } from "../../../theme/zIndex";
import { getReservationIdentityLabel } from "../utils/reservationLabels";

interface ReservationVoucherProps {
  reserva: Reservation;
  onClose: () => void;
}

const ReservationVoucher: React.FC<ReservationVoucherProps> = ({ reserva }) => {
  const theme = useTheme();
  const [departamentoInfo, setDepartamentoInfo] = useState<{
    zona?: string;
    imagen?: string;
    ubicacion?: string | null;
  } | null>(null);
  const [imagenError, setImagenError] = useState<boolean>(false);

  // Usar valor_total (precio actual) en lugar de valor_original (precio histórico)
  const valorTotalCalculado = Number(reserva?.valor_total) || 0;
  const saldoPendiente = valorTotalCalculado - Number(reserva.pago);

  // Calcular fechas para la frase resumen
  const fechaEntrada = parseDateOnly(reserva.entrada);
  const fechaSalida = parseDateOnly(reserva.salida);

  // Calcular número real de noches
  const cantidadNoches = differenceInDays(fechaSalida, fechaEntrada);
  const direccionDepartamento = departamentoInfo?.ubicacion;

  useEffect(() => {
    const cargarDepartamentoInfo = async () => {
      try {
        const [categories, metadata] = await Promise.all([
          fetchCategories(),
          fetchDepartmentInfo(String(reserva.departamento)).catch((error) => {
            logger.error("Error al cargar ubicación del departamento:", error);
            return null;
          }),
        ]);
        const infoZona = getDepartamentoInfo(reserva.departamento, categories);
        const infoImagen = getDepartamentoImagen(reserva.departamento);

        setDepartamentoInfo({
          ...infoZona,
          imagen: infoImagen?.imagen,
          ubicacion: metadata?.ubicacion ?? null,
        });
      } catch (error) {
        logger.error("Error al cargar información del departamento:", error);
        setDepartamentoInfo(null);
      }
    };
    cargarDepartamentoInfo();
  }, [reserva.departamento]);

  return (
    <Box
      sx={{
        transition:
          "background-color 0.3s ease, color 0.3s ease, transform 0.3s ease",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: { xs: "100vw", sm: 450 },
          mx: "auto",
          bgcolor: "background.paper",
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: (theme) => ({
            xs: `0 4px 20px ${
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.3)"
                : "rgba(0,0,0,0.1)"
            }`,
            sm: `0 10px 40px ${
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.4)"
                : "rgba(0,0,0,0.15)"
            }`,
          }),
          overflow: "hidden",
          fontFamily: "Roboto, sans-serif",
          display: "flex",
          flexDirection: "column",
          height: "auto",
          position: "relative",
          my: { xs: 2, sm: 0 },
          transition:
            "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        {/* Marca de agua */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-18deg)",
            opacity: 0.03,
            width: 220,
            pointerEvents: "none",
            zIndex: Z.modal + 1,
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={
              theme.palette.mode === "dark"
                ? "/brand/logo-white.png"
                : "/brand/watermark.png"
            }
            alt="Logo 679 Inn"
            style={{
              width: "200%",
              height: "auto",
              display: "block",
              filter:
                theme.palette.mode === "dark" ? "none" : "grayscale(100%)",
            }}
          />
        </Box>
        {/* Header */}
        <CardContent
          sx={{
            background:
              Number(reserva.departamento) <= 18
                ? `linear-gradient(135deg, ${theme.palette.zonas.centro.border} 0%, ${theme.palette.zonas.centro.light} 100%)` // Centro
                : `linear-gradient(135deg, ${theme.palette.zonas.loma.border} 0%, ${theme.palette.zonas.loma.light} 100%)`, // Loma
            color: "white",
            p: { xs: 1.25, sm: 2.5 },
            position: "relative",
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 17, sm: 20 },
                letterSpacing: 0.5,
              }}
            >
              Reserva Nº {reserva.id.toString().padStart(8, "0")}-0
            </Typography>
            <Chip
              label={reserva.estado}
              color={reserva.estado === "confirmada" ? "success" : "warning"}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: alpha(
                  reserva.estado === "confirmada"
                    ? theme.palette.success.main
                    : theme.palette.warning.main,
                  0.2
                ),
                color: "white",
                border: "1px solid",
                borderColor: alpha(
                  reserva.estado === "confirmada"
                    ? theme.palette.success.main
                    : theme.palette.warning.main,
                  0.3
                ),
              }}
            />
          </Stack>
          {/* Frase resumen */}
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            {`${format(fechaEntrada, "dd/MM/yyyy")} – ${format(
              fechaSalida,
              "dd/MM/yyyy"
            )} · ${reserva.cantidad_huespedes || 0} huésped${
              (reserva.cantidad_huespedes || 0) > 1 ? "es" : ""
            }`}
          </Typography>
        </CardContent>

        {/* Contenido principal */}
        <CardContent
          sx={{
            p: { xs: 0.75, sm: 3 },
            pt: { xs: 0.75, sm: 1.5 },
            flex: 1,
            overflowY: { xs: "auto", sm: "visible" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* Información del departamento */}
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.default",
            }}
          >
            <CardContent
              sx={{ p: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={5} sm={5}>
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "1/1",
                      borderRadius: 2,
                      overflow: "hidden",
                      maxWidth: { xs: 150, sm: 300 },
                      mx: "auto",
                    }}
                  >
                    {departamentoInfo?.imagen && !imagenError ? (
                      <img
                        src={departamentoInfo.imagen}
                        alt={`Departamento ${reserva.departamento}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={() => setImagenError(true)}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          bgcolor: theme.palette.grey[300],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Foto
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={7} sm={7}>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: { xs: 16, sm: 17 },
                      }}
                    >
                      Departamento {reserva.departamento}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <MeetingRoomIcon sx={{ fontSize: 16 }} />
                      {formatTipoCama(reserva.tipo_cama)}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <LocationOnIcon sx={{ fontSize: 17 }} />
                      {direccionDepartamento || "Dirección a confirmar"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 16 }} />
                      {reserva.acompanantes?.length || 0} acompañante
                      {(reserva.acompanantes?.length || 0) !== 1 ? "s" : ""}
                    </Typography>
                    {tieneDatosDeAfiliado(reserva) && (
                      <>
                        {/* Si sólo se cargó el documento, la línea del nombre se
                            omite en vez de quedar como "Afiliado:" en blanco. */}
                        {nombreCompletoAfiliado(reserva) && <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 16 }} />
                          Afiliado: {nombreCompletoAfiliado(reserva)}
                        </Typography>}
                        {reserva.afiliado_dni && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CreditCardIcon sx={{ fontSize: 16 }} />
                            DNI: {reserva.afiliado_dni}
                          </Typography>
                        )}
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Datos del titular y contacto */}
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.default",
            }}
          >
            <CardContent
              sx={{ p: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "text.primary", mb: 0.75, fontWeight: 600 }}
              >
                Datos del titular
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 14 }} /> Nombre completo
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      fontWeight: 500,
                      fontSize: "1rem",
                      ml: 3,
                    }}
                  >
                    {reserva.nombre} {reserva.apellido}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CreditCardIcon sx={{ fontSize: 14 }} /> 
                    {getReservationIdentityLabel(
                      reserva.cliente?.origen_reserva,
                      false
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      fontWeight: 500,
                      fontSize: "1rem",
                      ml: 3,
                    }}
                  >
                    {reserva.dni}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Detalles de estadía */}
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.default",
            }}
          >
            <CardContent
              sx={{ p: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "text.primary", mb: 0.75, fontWeight: 600 }}
              >
                Detalles de estadía
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={6}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 14 }} />
                        Ingreso
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "0.75rem",
                        }}
                      ></Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", fontWeight: 500, ml: 3 }}
                      >
                        {format(
                          parseDateOnly(reserva.entrada),
                          "dd/MM/yyyy",
                          { locale: es }
                        )}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 400,
                          ml: 3,
                          fontSize: "0.825rem",
                        }}
                      >
                        a las 14:00 hs
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 14 }} />
                        Egreso
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", fontWeight: 500, ml: 3 }}
                      >
                        {format(
                          parseDateOnly(reserva.salida),
                          "dd/MM/yyyy",
                          { locale: es }
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: 400,
                          ml: 3,
                          fontSize: "0.825rem",
                        }}
                      >
                        a las {reserva.late_checkout ? "18:00" : "10:00"} hs
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Stack spacing={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontSize: "0.75rem",
                            }}
                          >
                            <CalendarMonthIcon sx={{ fontSize: 14 }} />
                            Noches
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.primary",
                              fontWeight: 500,
                              ml: 3,
                            }}
                          >
                            {cantidadNoches}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Late Checkout Indicator - Alineado con Egreso */}
                      {reserva.late_checkout && (
                        <Grid item xs={12}>
                          <Box sx={{ mt: 1.4 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: theme.palette.text.secondary,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                fontSize: "0.75rem",
                              }}
                            >
                              <CalendarMonthIcon sx={{ fontSize: 14 }} />
                              Late Checkout
                              <Box
                                component="span"
                                sx={{
                                  color: theme.palette.success.main,
                                  ml: 0.5,
                                  fontSize: "0.7rem",
                                }}
                              >
                                ✅
                              </Box>
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Notas */}
          {reserva.notas && (
            <Card
              variant="outlined"
              sx={{
                bgcolor: "background.default",
              }}
            >
              <CardContent
                sx={{ p: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Notas
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {reserva.notas}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Información de pago */}
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.default",
            }}
          >
            <CardContent
              sx={{ p: { xs: 1.25, sm: 2 }, pb: { xs: 1, sm: 1.5 } }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Valor Total:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(valorTotalCalculado)}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Seña:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(reserva.pago || 0)}
                  </Typography>
                </Stack>

                <Divider sx={{ my: 0.5 }} />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">Saldo Pendiente:</Typography>
                  {saldoPendiente === 0 ? (
                    <Chip
                      label="Pagado"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip
                      label={formatCurrency(saldoPendiente)}
                      color={saldoPendiente > 0 ? "error" : "success"}
                      size="small"
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
 
          {/* Términos y Condiciones - Versión Refinada para Captura */}
          <Typography
            variant="caption"
            align="center"
            sx={{
              mt: 1,
              color: "text.primary",
              opacity: 0.8,
              display: "block",
              lineHeight: 1.3,
              fontSize: "0.65rem",
              px: 1,
            }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 800, color: "text.primary", mr: 0.5 }}
            >
              IMPORTANTE:
            </Box>
            Al confirmar la reserva o pago, el Huésped acepta los Términos y
            Condiciones en:{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: "text.primary" }}
            >
              679inn.com.ar/terminos-y-condiciones.html
            </Box>
          </Typography>

        </CardContent>
      </Card>
    </Box>
  );
};

export default ReservationVoucher;
